import React, { useState, useEffect, useRef } from 'react';
import { apiRequest } from '../../services/api';
import './InquiryModal.css';

interface InquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  artworkTitle: string;
  artworkImage: string;
}

const InquiryModal: React.FC<InquiryModalProps> = ({
  isOpen,
  onClose,
  artworkTitle,
  artworkImage,
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState(
    `Hi Micah, I'm interested in ${artworkTitle}.\nPlease share pricing, shipping, and any additional details.`
  );
  const [similarWorks, setSimilarWorks] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Reset form when modal opens with new artwork
  useEffect(() => {
    if (isOpen) {
      setName('');
      setEmail('');
      setMessage(
        `Hi Micah, I'm interested in ${artworkTitle}.\nPlease share pricing, shipping, and any additional details.`
      );
      setSimilarWorks(false);
      setSubmitted(false);
      setSubmitting(false);
      setError('');
    }
  }, [isOpen, artworkTitle]);

  // Focus trap & escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await apiRequest('/api/inquiry', {
        method: 'POST',
        body: JSON.stringify({
          name,
          email,
          message,
          artworkTitle,
          similarWorks,
        }),
      });
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="inquiry-modal" role="dialog" aria-modal="true" aria-labelledby="inquiry-modal-title">
      <div className="inquiry-modal__backdrop" onClick={onClose} />
      <div className="inquiry-modal__panel" ref={modalRef}>
        <button
          ref={closeRef}
          className="inquiry-modal__close"
          onClick={onClose}
          aria-label="Close inquiry form"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {submitted ? (
          <div className="inquiry-modal__success">
            <h2 className="inquiry-modal__title" id="inquiry-modal-title">Thank you!</h2>
            <p className="inquiry-modal__subtitle">
              Your inquiry has been sent. I'll get back to you soon.
            </p>
            <button className="inquiry-modal__cancel-btn" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <>
            <h2 className="inquiry-modal__title" id="inquiry-modal-title">Inquire about this work</h2>
            <p className="inquiry-modal__subtitle">
              Send a message and I'll get back to you with pricing, availability, and shipping details.
            </p>

            {/* Artwork card */}
            <div className="inquiry-modal__artwork-card">
              <img
                src={artworkImage}
                alt={artworkTitle}
                className="inquiry-modal__artwork-img"
                width={80}
                height={60}
              />
              <div className="inquiry-modal__artwork-info">
                <p className="inquiry-modal__artwork-title">{artworkTitle}</p>
                <p className="inquiry-modal__artwork-type">Original Painting</p>
              </div>
            </div>

            {/* Form */}
            <form className="inquiry-modal__form" onSubmit={handleSubmit}>
              <div className="inquiry-modal__field">
                <label htmlFor="inquiry-name" className="inquiry-modal__label">Full Name</label>
                <input
                  id="inquiry-name"
                  type="text"
                  className="inquiry-modal__input"
                  placeholder="Your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div className="inquiry-modal__field">
                <label htmlFor="inquiry-email" className="inquiry-modal__label">Email</label>
                <input
                  id="inquiry-email"
                  type="email"
                  className="inquiry-modal__input"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="inquiry-modal__field">
                <label htmlFor="inquiry-message" className="inquiry-modal__label">Message</label>
                <textarea
                  id="inquiry-message"
                  className="inquiry-modal__textarea"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  required
                />
              </div>

              <label className="inquiry-modal__checkbox-label">
                <input
                  type="checkbox"
                  className="inquiry-modal__checkbox"
                  checked={similarWorks}
                  onChange={(e) => setSimilarWorks(e.target.checked)}
                />
                <span>I'd also like to hear about similar available works</span>
              </label>

              <div className="inquiry-modal__actions">
                {error && <p className="inquiry-modal__error">{error}</p>}
                <button type="submit" className="inquiry-modal__submit-btn" disabled={submitting}>
                  {submitting ? 'Sending...' : 'Send Inquiry'}
                </button>
                <button type="button" className="inquiry-modal__cancel-btn" onClick={onClose} disabled={submitting}>
                  Cancel
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default InquiryModal;
